import { INTERNATIONAL_SHIPPING_NOTE } from '@/lib/shipping';
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Repeat, Tag, Check, X, Sparkles, HelpCircle, Gift } from "lucide-react";
import { SwipeableCartItem } from "@/components/SwipeableCartItem";
import { triggerHaptic, triggerSuccessHaptic } from "@/lib/haptics";
import { useCartStore, CartItemWithSubscription } from "@/stores/cartStore";
import { createStorefrontCheckout } from "@/lib/shopify";
import { toast } from "sonner";
import cartPopupLogo from "@/assets/cart-popup-logo.webp";
import LoyaltyPointsRedemption from "@/components/LoyaltyPointsRedemption";
import CartRecommendations from "@/components/cart/CartRecommendations";

// Comfort Grip products for upsell (3-pack variants)
const COMFORT_GRIPS = [
  {
    variantId: "gid://shopify/ProductVariant/41453068517509",
    productId: "gid://shopify/Product/6931105742981",
    title: "Black Pro Tour Grip",
    handle: "black-pro-tour-pickleball-over-grips",
    price: 7.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/Black_grip.webp?v=1763938310",
    color: "#1A1A1A"
  },
  {
    variantId: "gid://shopify/ProductVariant/41453069074565",
    productId: "gid://shopify/Product/6931101286533",
    title: "Neon Pink Pro Tour Grip",
    handle: "neon-pink-pickleball-over-grips",
    price: 7.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/Neon_Pink_Grip.webp?v=1763938362",
    color: "#FF1493"
  },
  {
    variantId: "gid://shopify/ProductVariant/41453068845189",
    productId: "gid://shopify/Product/6930941444229",
    title: "Bright White Pro Tour Grip",
    handle: "bright-white-overgrip",
    price: 7.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/Bright_White.webp?v=1765815237",
    fallbackImage: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/Bright_White_Grip_Packs_-_Grizzlee_Gripz.jpg?v=1764953970",
    color: "#FFFFFF"
  },
  {
    variantId: "gid://shopify/ProductVariant/41453069795461",
    productId: "gid://shopify/Product/6948632625285",
    title: "Royal Blue Pro Tour Grip",
    handle: "royal-blue-pro-tour-overgrips-12-pack",
    price: 7.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/Royal_Blue_Grip.webp?v=1763938319",
    color: "#1E4D8C"
  },
];

// Gradient Grip products for upsell
const GRADIENT_GRIPS = [
  {
    variantId: "gid://shopify/ProductVariant/41295522791557",
    productId: "gid://shopify/Product/7178321494149",
    title: "Blue to White Gradient",
    handle: "pro-tour-gradient-blue",
    price: 8.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/gradient-blue-grip-profile.png?v=1768966253",
    gradient: "linear-gradient(180deg, #1E4D8C 0%, #FFFFFF 100%)"
  },
  {
    variantId: "gid://shopify/ProductVariant/41295567454341",
    productId: "gid://shopify/Product/7178354131077",
    title: "Purple to White Gradient",
    handle: "pro-tour-purple-white-gradient-grips",
    price: 8.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/gradient-purple-grip-profile.png?v=1768966391",
    gradient: "linear-gradient(180deg, #8B5CF6 0%, #FFFFFF 100%)"
  },
  {
    variantId: "gid://shopify/ProductVariant/41295574990981",
    productId: "gid://shopify/Product/7178358980741",
    title: "Pink to White Gradient",
    handle: "pro-tour-pink-gradient-overgrips",
    price: 8.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/gradient-pink-grip-profile.png?v=1768967579",
    gradient: "linear-gradient(180deg, #FF69B4 0%, #FFFFFF 100%)"
  },
  {
    variantId: "gid://shopify/ProductVariant/41295577743493",
    productId: "gid://shopify/Product/7178360684677",
    title: "Yellow to White Gradient",
    handle: "pro-tour-yellow-gradient-overgrips",
    price: 8.99,
    image: "https://cdn.shopify.com/s/files/1/0564/6100/0837/files/gradient-yellow-grip-profile.png?v=1768966409",
    gradient: "linear-gradient(180deg, #FFD700 0%, #FFFFFF 100%)"
  },
];

// Bundle discount configuration
const BUNDLE_DISCOUNT_PERCENT = 15;

export const ShopifyCartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState(false);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [addingUpsell, setAddingUpsell] = useState<string | null>(null);
  
  const {
    items,
    isLoading,
    isSyncing,
    updateQuantity,
    removeItem,
    addItem,
    getCheckoutUrl,
    setCheckoutUrl,
    getTotalItems,
    getTotalPrice,
    getFinalPrice,
    getSubscriptionItems,
    syncCart,
    applyDiscountCode,
    applyLoyaltyPoints,
    removeLoyaltyPoints,
    loyaltyPointsApplied,
    loyaltyDiscountAmount,
    discountApplied,
    discountCode
  } = useCartStore();
  
  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();
  const subscriptionItems = getSubscriptionItems();
  const hasSubscriptions = subscriptionItems.length > 0;

  // Randomize upsell grips on drawer open
  const [randomComfortGrip, setRandomComfortGrip] = useState(COMFORT_GRIPS[0]);
  const [randomGradientGrip, setRandomGradientGrip] = useState(GRADIENT_GRIPS[0]);

  // Check if upsell items are already in cart
  const hasComfortInCart = items.some(item => 
    item.variantId === randomComfortGrip.variantId
  );
  const hasGradientInCart = items.some(item => 
    item.variantId === randomGradientGrip.variantId
  );

  // Calculate bundle pricing
  const bundleOriginalPrice = randomComfortGrip.price + randomGradientGrip.price;
  const bundleDiscountedPrice = bundleOriginalPrice * (1 - BUNDLE_DISCOUNT_PERCENT / 100);
  const bundleSavings = bundleOriginalPrice - bundleDiscountedPrice;

  // Track previous item count for detecting order completion
  const [prevItemCount, setPrevItemCount] = useState(items.length);

  // Randomize upsell products when drawer opens
  useEffect(() => {
    if (isOpen) {
      const randomComfort = COMFORT_GRIPS[Math.floor(Math.random() * COMFORT_GRIPS.length)];
      const randomGradient = GRADIENT_GRIPS[Math.floor(Math.random() * GRADIENT_GRIPS.length)];
      setRandomComfortGrip(randomComfort);
      setRandomGradientGrip(randomGradient);
      setPrevItemCount(items.length);
      syncCart();
    }
  }, [isOpen, syncCart]);

  // Show thank you message when cart is cleared after having items (order completed)
  useEffect(() => {
    if (prevItemCount > 0 && items.length === 0) {
      setShowThankYou(true);
    }
  }, [items.length, prevItemCount]);

  // Helper to ensure checkout URL has required channel parameter
  const formatCheckoutUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('channel', 'online_store');
      return urlObj.toString();
    } catch {
      return url;
    }
  };

  const createUpsellItem = (grip: typeof COMFORT_GRIPS[0] | typeof GRADIENT_GRIPS[0]): CartItemWithSubscription => ({
    product: {
      node: {
        id: grip.productId,
        title: grip.title,
        description: "Premium pickleball overgrip",
        handle: grip.handle,
        priceRange: {
          minVariantPrice: {
            amount: grip.price.toString(),
            currencyCode: "USD"
          }
        },
        images: {
          edges: [{
            node: {
              url: grip.image,
              altText: grip.title
            }
          }]
        },
        variants: {
          edges: [{
            node: {
              id: grip.variantId,
              title: "3 PK",
              price: {
                amount: grip.price.toString(),
                currencyCode: "USD"
              },
              availableForSale: true,
              selectedOptions: [{
                name: "Quantity",
                value: "3 PK"
              }]
            }
          }]
        },
        options: [{
          name: "Quantity",
          values: ["3 PK"]
        }]
      }
    },
    variantId: grip.variantId,
    variantTitle: "3 PK",
    price: {
      amount: grip.price.toString(),
      currencyCode: "USD"
    },
    quantity: 1,
    selectedOptions: [{
      name: "Quantity",
      value: "3 PK"
    }]
  });

  const handleAddUpsell = async (type: 'comfort' | 'gradient' | 'bundle') => {
    setAddingUpsell(type);
    try {
      if (type === 'comfort') {
        await addItem(createUpsellItem(randomComfortGrip));
        toast.success(`Added ${randomComfortGrip.title}!`, { position: "top-center" });
      } else if (type === 'gradient') {
        await addItem(createUpsellItem(randomGradientGrip));
        toast.success(`Added ${randomGradientGrip.title}!`, { position: "top-center" });
      } else if (type === 'bundle') {
        // Add items sequentially — wait for first to fully complete before second
        await addItem(createUpsellItem(randomComfortGrip));
        // Small delay to ensure Shopify cart state is settled
        await new Promise(resolve => setTimeout(resolve, 300));
        await addItem(createUpsellItem(randomGradientGrip));
        toast.success("Added Grip Bundle! Discount applied at checkout.", { position: "top-center" });
      }
      triggerSuccessHaptic();
    } catch (error) {
      console.error('Failed to add upsell:', error);
      toast.error("Failed to add item. Please try again.");
    } finally {
      setAddingUpsell(null);
    }
  };

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    let checkoutUrl = getCheckoutUrl();

    if (!checkoutUrl) {
      setIsCreatingCheckout(true);
      try {
        console.log("Creating checkout with items:", items);
        checkoutUrl = await createStorefrontCheckout(items);
        console.log("Created checkout URL:", checkoutUrl);
        if (checkoutUrl) {
          setCheckoutUrl(checkoutUrl);
        }
      } catch (error) {
        console.error("Failed to create checkout:", error);
        toast.error("Failed to create checkout. Please try again.");
        setIsCreatingCheckout(false);
        return;
      }
      setIsCreatingCheckout(false);
    }

    // Auto-apply subscription discount code if subscription items are in cart
    if (hasSubscriptions && !discountApplied) {
      const currentCartId = useCartStore.getState().cartId;
      if (currentCartId) {
        try {
          const result = await applyDiscountCode('GG-SUBSCRIBE-SAVE');
          if (result.success) {
            console.log("Auto-applied subscription discount GG-SUBSCRIBE-SAVE");
          }
        } catch (error) {
          console.error("Failed to auto-apply subscription discount:", error);
        }
      }
    }
    
    if (checkoutUrl) {
      const formattedUrl = formatCheckoutUrl(checkoutUrl);
      console.log("Opening checkout URL:", formattedUrl);
      triggerSuccessHaptic();
      setIsOpen(false);

      const checkoutWindow = window.open(formattedUrl, '_blank');
      if (!checkoutWindow || checkoutWindow.closed || typeof checkoutWindow.closed === 'undefined') {
        console.log("Popup blocked, redirecting in same page...");
        toast.info("Redirecting to checkout...", { position: "top-center" });
        window.location.href = formattedUrl;
        return;
      }
      toast.success("Checkout opened!", {
        description: "Complete your purchase in the new window.",
        position: "top-center"
      });
    } else {
      triggerHaptic('heavy');
      toast.error("Could not create checkout. Please try again.");
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    const cleanCode = promoCode.trim().toUpperCase();
    if (cleanCode.length > 50) {
      setPromoError("Promo code is too long");
      return;
    }
    setIsApplyingPromo(true);
    setPromoError("");
    setPromoSuccess(false);
    try {
      const result = await applyDiscountCode(cleanCode);
      if (result.success) {
        setPromoSuccess(true);
        triggerSuccessHaptic();
        toast.success("Promo code applied!", { position: "top-center" });
      } else {
        triggerHaptic('heavy');
        setPromoError(result.error || "Invalid promo code");
      }
    } catch {
      setPromoError("Failed to apply promo code");
    } finally {
      setIsApplyingPromo(false);
    }
  };

  // Show upsells only if both items are not in cart
  const showUpsells = !hasComfortInCart || !hasGradientInCart;
  const showBundleOption = !hasComfortInCart && !hasGradientInCart;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <SheetTrigger asChild>
            <button aria-label="View shopping cart" className="relative flex items-center justify-center w-10 h-10 rounded-full border border-primary/30 bg-background transition-all hover:ring-2 hover:ring-primary shadow-[0_4px_15px_rgba(0,0,0,0.15)]">
              <ShoppingCart className="w-5 h-5" aria-hidden="true" />
              {totalItems > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-primary text-primary-foreground">
                  {totalItems}
                </Badge>
              )}
            </button>
          </SheetTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom"><p>Shopping Cart</p></TooltipContent>
      </Tooltip>
      
      <SheetContent className="!w-screen sm:!w-[520px] lg:!w-[580px] !max-w-[100vw] flex flex-col h-[100dvh] max-h-[100dvh] overflow-hidden p-0">
        {/* Checkout Loading Overlay */}
        {isCreatingCheckout && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-sm">
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
              </div>
              <h3 className="mb-1 text-lg font-bold">Preparing Checkout</h3>
              <p className="text-sm text-muted-foreground">Opening secure checkout...</p>
            </div>
          </div>
        )}

        {/* Header with Logo and Help Icon */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border/50 px-3 py-2.5 pr-12 sm:px-4">
          <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
            <img src={cartPopupLogo} alt="Grizzlee Gripz" className="h-8 w-8 flex-shrink-0 object-contain sm:h-10 sm:w-10" />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <SheetTitle className="text-base leading-none sm:text-lg">Shopping Cart</SheetTitle>
                <Link 
                  to="/contact" 
                  onClick={() => setIsOpen(false)}
                  className="flex-shrink-0"
                  aria-label="Contact support"
                  title="Need Help?"
                >
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </Link>
              </div>
              <SheetDescription className="mt-1 text-xs sm:text-sm">
                {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''}`}
              </SheetDescription>
            </div>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {items.length === 0 ? (
            <div className="flex h-full items-center justify-center p-6">
              <div className="text-center">
                <ShoppingCart className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              <div className="space-y-3 px-3 py-3 pb-8 sm:space-y-4 sm:px-4 sm:py-4">
                {/* Cart Items */}
                <div className="space-y-2.5">
                  {items.map(item => (
                    <SwipeableCartItem
                      key={`${item.variantId}-${item.subscription?.enabled ? 'sub' : 'one-time'}`}
                      item={item}
                      onRemove={removeItem}
                      onUpdateQuantity={updateQuantity}
                    />
                  ))}
                </div>

                {/* Upsell Section — hidden; replaced by CartRecommendations ("Complete Your Game") */}
                {false && showUpsells && (
                  <div className="rounded-lg border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 p-3">
                    <div className="mb-3 flex items-center gap-2">
                      <Gift className="h-4 w-4 text-primary" />
                      <span className="text-sm font-semibold text-foreground">Complete Your Grip Collection!</span>
                    </div>
                    
                    <div className="space-y-2.5">
                      {/* Individual Comfort Grip */}
                      {!hasComfortInCart && (
                        <div className="flex items-start gap-3 rounded-md bg-background/60 p-2.5">
                          <div 
                            className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md border sm:h-12 sm:w-12"
                            style={{ backgroundColor: randomComfortGrip.color === '#FFFFFF' ? '#f5f5f5' : randomComfortGrip.color }}
                          >
                            <img 
                              src={randomComfortGrip.image} 
                              alt={randomComfortGrip.title} 
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-xs font-medium leading-tight whitespace-normal break-words text-foreground sm:text-sm">{randomComfortGrip.title}</h4>
                            <p className="mt-1 text-xs text-muted-foreground">Comfort Grip • 3-Pack</p>
                            <span className="mt-1 block text-sm font-bold text-primary">${randomComfortGrip.price.toFixed(2)}</span>
                          </div>
                          <Button 
                            onClick={() => handleAddUpsell('comfort')} 
                            size="sm" 
                            variant="outline"
                            className="h-8 shrink-0 rounded-full px-2.5 text-xs sm:h-9 sm:px-4 sm:text-sm"
                            disabled={addingUpsell === 'comfort' || isLoading}
                          >
                            {addingUpsell === 'comfort' ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                          </Button>
                        </div>
                      )}

                      {/* Individual Gradient Grip */}
                      {!hasGradientInCart && (
                        <div className="flex items-start gap-3 rounded-md bg-background/60 p-2.5">
                          <div 
                            className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md border sm:h-12 sm:w-12"
                            style={{ background: randomGradientGrip.gradient }}
                          >
                            <img 
                              src={randomGradientGrip.image} 
                              alt={randomGradientGrip.title} 
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-xs font-medium leading-tight whitespace-normal break-words text-foreground sm:text-sm">{randomGradientGrip.title}</h4>
                            <p className="mt-1 text-xs text-muted-foreground">Gradient Grip • 3-Pack</p>
                            <span className="mt-1 block text-sm font-bold text-primary">${randomGradientGrip.price.toFixed(2)}</span>
                          </div>
                          <Button 
                            onClick={() => handleAddUpsell('gradient')} 
                            size="sm" 
                            variant="outline"
                            className="h-8 shrink-0 rounded-full px-2.5 text-xs sm:h-9 sm:px-4 sm:text-sm"
                            disabled={addingUpsell === 'gradient' || isLoading}
                          >
                            {addingUpsell === 'gradient' ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                          </Button>
                        </div>
                      )}

                      {/* Bundle Option */}
                      {showBundleOption && (
                        <div className="mt-2 border-t border-primary/20 pt-2.5">
                          <div className="flex flex-col gap-2 rounded-md bg-primary/5 p-2.5 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-start gap-2">
                              <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium leading-tight whitespace-normal break-words sm:text-sm">Add Both & Save {BUNDLE_DISCOUNT_PERCENT}%</p>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-bold text-primary">${bundleDiscountedPrice.toFixed(2)}</span>
                                  <span className="text-xs text-muted-foreground line-through">${bundleOriginalPrice.toFixed(2)}</span>
                                  <Badge className="bg-primary px-1.5 py-0 text-[10px] text-primary-foreground">
                                    SAVE ${bundleSavings.toFixed(2)}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button 
                              onClick={() => handleAddUpsell('bundle')} 
                              size="sm" 
                              className="h-8 shrink-0 self-start rounded-full px-3 text-xs btn-fill-rise btn-fill-dark sm:h-9 sm:self-center sm:px-4 sm:text-sm"
                              disabled={addingUpsell === 'bundle' || isLoading}
                            >
                              {addingUpsell === 'bundle' ? <Loader2 className="h-3 w-3 animate-spin" /> : "Bundle"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Promo Code Section */}
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <div className="relative min-w-0 flex-1">
                      <Tag className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Promo code"
                        value={promoCode}
                        onChange={e => {
                          setPromoCode(e.target.value);
                          setPromoError("");
                        }}
                        className="h-9 pl-9 text-xs uppercase sm:h-10"
                        maxLength={50}
                        disabled={discountApplied || promoSuccess}
                      />
                    </div>
                    <Button
                      onClick={handleApplyPromo}
                      variant="outline"
                      size="sm"
                      disabled={isApplyingPromo || discountApplied || promoSuccess}
                      className="h-9 shrink-0 px-3 text-xs sm:h-10"
                    >
                      {isApplyingPromo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : discountApplied || promoSuccess ? <Check className="h-3.5 w-3.5 text-primary" /> : "Apply"}
                    </Button>
                  </div>
                  {promoError && (
                    <p className="flex items-start gap-1 text-[11px] leading-tight text-destructive">
                      <X className="mt-0.5 h-3 w-3 flex-shrink-0" /> <span>{promoError}</span>
                    </p>
                  )}
                  {(discountApplied || promoSuccess) && (
                    <p className="flex items-start gap-1 text-[11px] leading-tight text-primary">
                      <Check className="mt-0.5 h-3 w-3 flex-shrink-0" /> <span>Promo code "{discountCode || promoCode}" applied!</span>
                    </p>
                  )}
                </div>

                {/* Subscription Notice */}
                {hasSubscriptions && (
                  <div className="rounded-md border border-primary/20 bg-primary/5 p-2.5">
                    <div className="flex items-start gap-2">
                      <Repeat className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                      <div className="text-[11px] leading-tight sm:text-xs">
                        <p className="font-medium text-foreground">Subscription items included</p>
                        <p className="mt-0.5 text-muted-foreground">
                          Billed monthly for 3 months. Month 1 charged at checkout.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <CartRecommendations />

                {/* Loyalty Points Redemption */}
                <LoyaltyPointsRedemption
                  cartTotal={totalPrice}
                  onApplyDiscount={(discountAmount, pointsUsed) => {
                    applyLoyaltyPoints(pointsUsed, discountAmount);
                  }}
                  appliedPoints={loyaltyPointsApplied}
                  onRemoveDiscount={removeLoyaltyPoints}
                />

                {/* Total Display */}
                <div className="space-y-2 border-t pt-2.5">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-right">${totalPrice.toFixed(2)}</span>
                  </div>
                  {loyaltyDiscountAmount > 0 && (
                    <div className="flex items-center justify-between gap-3 text-sm text-primary">
                      <span>Rewards Discount</span>
                      <span className="text-right">-${loyaltyDiscountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    {totalPrice >= 50 ? (
                      <span className="font-medium text-primary">FREE ✓</span>
                    ) : (
                      <span className="text-right text-xs text-muted-foreground">Free on US orders $50+</span>
                    )}
                  </div>
                  {totalPrice < 50 && (
                    <div className="space-y-1">
                      <div className="h-1.5 w-full rounded-full bg-muted">
                        <div 
                          className="h-1.5 rounded-full bg-primary transition-all duration-300" 
                          style={{ width: `${Math.min(100, (totalPrice / 50) * 100)}%` }}
                        />
                      </div>
                      <p className="text-center text-[10px] leading-tight text-muted-foreground">
                        Add <span className="font-semibold text-foreground">${(50 - totalPrice).toFixed(2)}</span> more for free US shipping
                      </p>
                    </div>
                  )}
                  <p className="text-center text-[10px] leading-tight text-muted-foreground">
                    {INTERNATIONAL_SHIPPING_NOTE}
                  </p>
                  <div className="flex items-center justify-between gap-3 border-t pt-2">
                    <span className="text-sm font-semibold sm:text-base">
                      {hasSubscriptions ? "Today's Total" : "Total"}
                    </span>
                    <span className="text-right text-base font-bold text-primary sm:text-lg">
                      ${getFinalPrice().toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Checkout Button */}
                <Button
                  onClick={handleCheckout}
                  className="w-full rounded-full text-sm btn-fill-rise btn-fill-dark"
                  size="default"
                  disabled={items.length === 0 || isLoading || isSyncing || isCreatingCheckout}
                >
                  {isLoading || isSyncing || isCreatingCheckout ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isCreatingCheckout ? 'Creating Checkout...' : isSyncing ? 'Syncing...' : 'Loading...'}
                    </>
                  ) : (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Checkout
                    </>
                  )}
                </Button>

                {/* Footer */}
                <p className="text-center text-[10px] text-muted-foreground">
                  Secure checkout powered by Shopify
                </p>

                {/* Thank You Message */}
                {showThankYou && items.length === 0 && (
                  <div className="mt-4 rounded-lg border border-primary/20 bg-gradient-to-r from-primary/10 to-accent/10 p-4 text-center animate-fade-in">
                    <div className="mb-2 flex justify-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
                        <Check className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                    <h3 className="mb-1 text-lg font-bold text-foreground">Thank You for Your Order! 🎉</h3>
                    <p className="mb-2 text-sm text-muted-foreground">
                      Your order has been successfully placed!
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Check your email for order confirmation and tracking details.
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        setShowThankYou(false);
                        setIsOpen(false);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
